/**
 * Aerodrome Service
 * 
 * Frontend API client for the aerodrome registry.
 * Handles seed, enrichment, search, and source URL management.
 */

import api from './api';

// ─── Types ──────────────────────────────────────────────────────────

export interface AerodromeData {
  id: string;
  icao_code: string;
  iata_code: string | null;
  name: string | null;
  airport_type: string | null;
  latitude: number | null;
  longitude: number | null;
  elevation_ft: number | null;
  country_code: string | null;
  municipality: string | null;
  runways: Record<string, unknown>[];
  taxiways: Record<string, unknown>[];
  stands: Record<string, unknown>[];
  frequencies: Record<string, unknown>[];
  navaids: Record<string, unknown>[];
  cns_data: Record<string, unknown>;
  restrictions: Record<string, unknown>[];
  max_aircraft_pcn: string | null;
  data_source: string;
  airac_cycle: string | null;
  confidence: number;
  parsed_sections: string[];
  last_seed_at: string | null;
  last_enriched_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AerodromeSummary {
  icao_code: string;
  iata_code: string | null;
  name: string | null;
  country_code: string | null;
  data_source: string;
  confidence: number;
  has_runways: boolean;
  has_stands: boolean;
  has_taxiways: boolean;
}

export interface AIPSource {
  id: string;
  icao_code: string;
  url: string;
  source_type: string;
  portal_name: string | null;
  last_fetch_at: string | null;
  last_fetch_status: string;
  last_error: string | null;
  airac_cycle: string | null;
  notes: string | null;
  created_at: string | null;
}

export interface AerodromeStats {
  total_aerodromes: number;
  seeded_ourairports: number;
  enriched_aip: number;
  with_stands: number;
  airac: {
    current_cycle: string;
    current_cycle_start: string;
    next_cycle_start: string;
    days_until_next: number;
  };
}

export interface BatchSeedResult {
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  total: number;
}

// ─── Service ────────────────────────────────────────────────────────

export const aerodromeService = {

  /**
   * Get aerodrome data by ICAO code.
   * Returns null if not found (404).
   */
  async get(icao: string): Promise<AerodromeData | null> {
    try {
      const res = await api.get<AerodromeData>(`/aerodrome/${icao.toUpperCase()}`);
      return res.data;
    } catch (err: any) {
      if (err.response?.status === 404) return null;
      throw err;
    }
  },

  /**
   * Search aerodromes by name, ICAO, or IATA code.
   */
  async search(query: string, limit = 20): Promise<AerodromeSummary[]> {
    const res = await api.get<AerodromeSummary[]>(`/aerodrome/search/query`, {
      params: { q: query, limit },
    });
    return res.data;
  },

  /**
   * Get aerodrome database stats + AIRAC info.
   */
  async getStats(): Promise<AerodromeStats> {
    const res = await api.get<AerodromeStats>('/aerodrome/stats/overview');
    return res.data;
  },

  /**
   * Seed a single aerodrome from OurAirports.
   */
  async seedSingle(icao: string): Promise<AerodromeData | null> {
    try {
      const res = await api.post<AerodromeData>(
        `/aerodrome/seed/${icao.toUpperCase()}`,
        {},
        { timeout: 60000 } // OurAirports download can be slow
      );
      return res.data;
    } catch (err: any) {
      if (err.response?.status === 404) return null;
      throw err;
    }
  },

  /**
   * Batch seed aerodromes from OurAirports.
   */
  async seedBatch(
    airportTypes: string[] = ['large_airport', 'medium_airport'],
    countryCodes?: string[]
  ): Promise<BatchSeedResult> {
    const res = await api.post<BatchSeedResult>(
      '/aerodrome/seed/batch/run',
      { airport_types: airportTypes, country_codes: countryCodes || null },
      { timeout: 300000 } // 5 min for large batch
    );
    return res.data;
  },

  /**
   * Enrich aerodrome from a registered AIP source URL.
   */
  async enrichFromRegistry(icao: string): Promise<AerodromeData> {
    const res = await api.post<AerodromeData>(
      `/aerodrome/${icao.toUpperCase()}/enrich`,
      {},
      { timeout: 120000 } // PDF download + parse
    );
    return res.data;
  },

  /**
   * Enrich aerodrome from uploaded AIP PDF.
   */
  async enrichFromUpload(icao: string, file: File): Promise<AerodromeData> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post<AerodromeData>(
      `/aerodrome/${icao.toUpperCase()}/enrich/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000, // 10 min for large AIP PDFs
      }
    );
    return res.data;
  },

  /**
   * Register an AIP source URL for an aerodrome.
   */
  async registerSource(icao: string, source: {
    url: string;
    source_type?: string;
    portal_name?: string;
    notes?: string;
  }): Promise<AIPSource> {
    const res = await api.post<AIPSource>(
      `/aerodrome/${icao.toUpperCase()}/source`,
      source
    );
    return res.data;
  },

  /**
   * List registered AIP source URLs for an aerodrome.
   */
  async listSources(icao: string): Promise<AIPSource[]> {
    const res = await api.get<AIPSource[]>(`/aerodrome/${icao.toUpperCase()}/sources`);
    return res.data;
  },

  /**
   * Delete a registered AIP source URL.
   */
  async deleteSource(icao: string, sourceId: string): Promise<void> {
    await api.delete(`/aerodrome/${icao.toUpperCase()}/source/${sourceId}`);
  },

  /**
   * Get AIRAC cycle information.
   */
  async getAiracInfo(): Promise<AerodromeStats['airac']> {
    const res = await api.get<AerodromeStats['airac']>('/aerodrome/airac/info');
    return res.data;
  },
};
